import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, addDoc, getDocs, deleteDoc, doc } from 'firebase/firestore';

// API URL - uses api subdomain in production, localhost in development
const API_URL = process.env.NODE_ENV === 'production'
    ? 'https://api.pythagoreschool.com/upload.php'
    : 'http://localhost:3001/upload';

const NotificationManagement = ({ classes = [], students = [], enrollments = [] }) => {
    const [notifications, setNotifications] = useState([]);
    const [title, setTitle] = useState('');
    const [message, setMessage] = useState('');
    const [selectedClass, setSelectedClass] = useState('');
    const [classStudents, setClassStudents] = useState([]);
    const [selectedStudents, setSelectedStudents] = useState([]);
    const [selectAllStudents, setSelectAllStudents] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [notificationToDelete, setNotificationToDelete] = useState(null);
    const [loading, setLoading] = useState(false);

    // File attachment states
    const [attachedFile, setAttachedFile] = useState(null);
    const [filePreview, setFilePreview] = useState(null);
    const [uploadProgress, setUploadProgress] = useState(0);

    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const ITEMS_PER_PAGE = 5;

    useEffect(() => {
        fetchNotifications();
    }, []);

    // Effect to fetch students when class is selected
    useEffect(() => {
        if (selectedClass && selectedClass !== 'all') {
            // تصفية التلامذة بناءً على enrollments للفصل المحدد
            const enrolledStudentIds = enrollments
                .filter(enrollment => enrollment.classId === selectedClass)
                .map(enrollment => enrollment.studentId);

            const studentsInClass = students.filter(student =>
                enrolledStudentIds.includes(student.id)
            );

            console.log('Students in selected class:', studentsInClass);
            console.log('Selected class ID:', selectedClass);
            console.log('Enrollments:', enrollments.filter(e => e.classId === selectedClass));

            setClassStudents(studentsInClass);
            setSelectedStudents([]);
            setSelectAllStudents(false);
        } else if (selectedClass === 'all') {
            // عند اختيار "كل الفصول"، نعرض جميع التلامذة المسجلين في أي فصل
            const enrolledStudentIds = enrollments.map(enrollment => enrollment.studentId);
            const allEnrolledStudents = students.filter(student =>
                enrolledStudentIds.includes(student.id)
            );

            console.log('All enrolled students:', allEnrolledStudents);
            setClassStudents(allEnrolledStudents);
            setSelectedStudents([]);
            setSelectAllStudents(false);
        } else {
            setClassStudents([]);
            setSelectedStudents([]);
            setSelectAllStudents(false);
        }
    }, [selectedClass, students, enrollments]);

    const fetchNotifications = async () => {
        try {
            const querySnapshot = await getDocs(collection(db, 'notifications'));
            const data = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                createdAt: doc.data().createdAt?.toDate()
            }));
            setNotifications(data.sort((a, b) => b.createdAt - a.createdAt));
        } catch (error) {
            console.error('Error fetching notifications:', error);
        }
    };

    const handleStudentSelection = (studentId) => {
        setSelectedStudents(prev => {
            if (prev.includes(studentId)) {
                return prev.filter(id => id !== studentId);
            } else {
                return [...prev, studentId];
            }
        });
    };

    const handleSelectAllStudents = () => {
        if (selectAllStudents) {
            setSelectedStudents([]);
        } else {
            setSelectedStudents(classStudents.map(student => student.id));
        }
        setSelectAllStudents(!selectAllStudents);
    };

    // Handle file selection
    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validate file type
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'application/pdf'];
        if (!allowedTypes.includes(file.type)) {
            alert('نوع الملف غير مدعوم. يُسمح فقط بالصور (JPG, PNG, GIF) أو PDF');
            return;
        }

        // Validate file size (max 5MB)
        const maxSize = 5 * 1024 * 1024; // 5MB
        if (file.size > maxSize) {
            alert('حجم الملف كبير جداً. الحد الأقصى 5MB');
            return;
        }

        setAttachedFile(file);

        // Create preview for images
        if (file.type.startsWith('image/')) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setFilePreview(reader.result);
            };
            reader.readAsDataURL(file);
        } else {
            setFilePreview(null);
        }
    };

    // Remove attached file
    const handleRemoveFile = () => {
        setAttachedFile(null);
        setFilePreview(null);
    };

    // Upload file to API Server
    const handleFileUpload = async () => {
        if (!attachedFile) return null;

        const formData = new FormData();
        formData.append('file', attachedFile);

        try {
            setUploadProgress(10);
            const response = await fetch(API_URL, {
                method: 'POST',
                body: formData
            });

            setUploadProgress(80);

            if (!response.ok) {
                throw new Error('Upload failed');
            }

            const data = await response.json();
            setUploadProgress(100);

            setTimeout(() => setUploadProgress(0), 500);

            return {
                url: data.fileUrl,
                name: attachedFile.name,
                type: attachedFile.type,
                size: attachedFile.size
            };
        } catch (error) {
            console.error('Error uploading file:', error);
            setUploadProgress(0);
            throw error;
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!title.trim() || !message.trim() || !selectedClass) {
            alert('الرجاء تعبئة جميع الحقول');
            return;
        }

        // If class is selected but no students are selected (when not "all" classes)
        if (selectedClass !== 'all' && selectedStudents.length === 0) {
            alert('الرجاء اختيار تلميذ واحد على الأقل');
            return;
        }

        setLoading(true);
        try {
            // Upload file if attached
            let attachmentData = null;
            if (attachedFile) {
                attachmentData = await handleFileUpload();
            }

            const notificationData = {
                title,
                message,
                createdAt: new Date(),
                status: 'unread',
                type: 'admin'
            };

            // Add attachment data if file was uploaded
            if (attachmentData) {
                notificationData.attachment = attachmentData;
            }

            if (selectedClass === 'all') {
                notificationData.className = 'كل الفصول';
                notificationData.recipients = {
                    type: 'all_classes'
                };
            } else {
                const selectedClassObj = classes.find(c => c.id === selectedClass);
                notificationData.classId = selectedClass;
                notificationData.className = selectedClassObj.name;

                if (selectedStudents.length === classStudents.length) {
                    // All students in class are selected
                    notificationData.recipients = {
                        type: 'class',
                        classId: selectedClass
                    };
                } else if (selectedStudents.length === 1) {
                    // Only one student is selected
                    notificationData.recipients = {
                        type: 'student',
                        studentId: selectedStudents[0]
                    };
                    const student = classStudents.find(s => s.id === selectedStudents[0]);
                    notificationData.studentNames = student ? `${student.firstName} ${student.lastName}` : '';
                } else if (selectedStudents.length > 1) {
                    // multiple specific students are selected
                    notificationData.recipients = {
                        type: 'students',
                        classId: selectedClass,
                        studentIds: selectedStudents
                    };

                    // Add student names for display in the notification list
                    const studentNames = selectedStudents.map(id => {
                        const student = classStudents.find(s => s.id === id);
                        return student ? `${student.firstName} ${student.lastName}` : '';
                    }).filter(name => name);

                    notificationData.studentNames = studentNames.join(', ');
                }
            }

            await addDoc(collection(db, 'notifications'), notificationData);

            setTitle('');
            setMessage('');
            setSelectedClass('');
            setSelectedStudents([]);
            setSelectAllStudents(false);
            setAttachedFile(null);
            setFilePreview(null);
            await fetchNotifications();
        } catch (error) {
            console.error('Error adding notification:', error);
            alert('حدث خطأ أثناء إرسال الإشعار');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = (notification) => {
        setNotificationToDelete(notification);
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        if (!notificationToDelete) return;

        try {
            await deleteDoc(doc(db, 'notifications', notificationToDelete.id));
            setNotifications(notifications.filter(n => n.id !== notificationToDelete.id));
        } catch (error) {
            console.error('Error deleting notification:', error);
            alert('حدث خطأ أثناء حذف الإشعار');
        }
        setShowDeleteModal(false);
        setNotificationToDelete(null);
    };

    // Calculate pagination
    const totalPages = Math.ceil(notifications.length / ITEMS_PER_PAGE);
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    const currentNotifications = notifications.slice(startIndex, endIndex);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        // Scroll to top of notifications list
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    return (
        <div className="p-6 bg-gray-100 min-h-screen" dir="rtl">
            <div className="max-w-4xl mx-auto">
                <h2 className="text-2xl font-bold mb-6">إدارة الإشعارات</h2>

                {/* نموذج إضافة إشعار جديد */}
                <form onSubmit={handleSubmit} className="bg-white p-6 rounded-lg shadow-md mb-8">
                    <div className="space-y-4">
                        <div>
                            <label className="block text-gray-700 font-semibold mb-2">عنوان الإشعار</label>
                            <input
                                type="text"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-gray-700 font-semibold mb-2">الفصل</label>
                            <select
                                value={selectedClass}
                                onChange={(e) => setSelectedClass(e.target.value)}
                                className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 bg-white"
                                required
                            >
                                <option value="">اختر الفصل</option>
                                <option value="all">كل الفصول</option>
                                {classes.map(c => (
                                    <option key={c.id} value={c.id}>{c.name}</option>
                                ))}
                            </select>
                        </div>

                        {/* قائمة اختيار التلاميذ - تظهر عند اختيار أي فصل */}
                        {selectedClass && (
                            <div className="mt-4">
                                <div className="flex items-center justify-between mb-2">
                                    <label className="block text-gray-700 font-semibold">اختيار التلاميذ</label>
                                    <div className="flex items-center">
                                        <input
                                            type="checkbox"
                                            id="selectAll"
                                            checked={selectAllStudents}
                                            onChange={handleSelectAllStudents}
                                            className="mr-2"
                                        />
                                        <label htmlFor="selectAll" className="text-sm">اختيار الكل</label>
                                    </div>
                                </div>
                                <div className="max-h-48 overflow-y-auto border rounded p-2">
                                    {classStudents.length > 0 ? (
                                        <div className="space-y-2">
                                            {classStudents.map(student => (
                                                <div key={student.id} className="flex items-center">
                                                    <input
                                                        type="checkbox"
                                                        id={`student-${student.id}`}
                                                        checked={selectedStudents.includes(student.id)}
                                                        onChange={() => handleStudentSelection(student.id)}
                                                        className="mr-2"
                                                    />
                                                    <label htmlFor={`student-${student.id}`}>
                                                        {student.firstName} {student.lastName}
                                                    </label>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-gray-500 text-center py-2">لا يوجد تلاميذ في هذا الفصل</p>
                                    )}
                                </div>
                                <div className="text-sm text-gray-600 mt-1">
                                    تم اختيار {selectedStudents.length} من {classStudents.length} تلميذ
                                </div>
                            </div>
                        )}

                        <div>
                            <label className="block text-gray-700 font-semibold mb-2">نص الإشعار</label>
                            <textarea
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
                                rows="4"
                                required
                            ></textarea>
                        </div>

                        {/* File attachment section */}
                        <div>
                            <label className="block text-gray-700 font-semibold mb-2">
                                إرفاق ملف (اختياري)
                                <span className="text-xs text-gray-500 mr-2">صورة أو PDF - الحد الأقصى 5MB</span>
                            </label>
                            <input
                                type="file"
                                accept="image/jpeg,image/jpg,image/png,image/gif,application/pdf"
                                onChange={handleFileChange}
                                className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
                            />

                            {/* File preview */}
                            {attachedFile && (
                                <div className="mt-3 p-3 bg-gray-50 border rounded">
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                            {filePreview ? (
                                                <div className="mb-2">
                                                    <img
                                                        src={filePreview}
                                                        alt="Preview"
                                                        className="max-h-40 rounded border"
                                                    />
                                                </div>
                                            ) : (
                                                <div className="flex items-center text-red-600 mb-2">
                                                    <svg className="w-8 h-8 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                                        <path d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" />
                                                    </svg>
                                                    <span className="font-medium">ملف PDF</span>
                                                </div>
                                            )}
                                            <div className="text-sm text-gray-600">
                                                <strong>{attachedFile.name}</strong><br />
                                                الحجم: {(attachedFile.size / 1024).toFixed(2)} KB
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={handleRemoveFile}
                                            className="text-red-500 hover:text-red-700 p-1"
                                            title="إزالة الملف"
                                        >
                                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Upload progress bar */}
                        {uploadProgress > 0 && (
                            <div className="mt-2">
                                <div className="flex items-center justify-between mb-1">
                                    <span className="text-sm text-blue-600 font-medium">جاري الرفع...</span>
                                    <span className="text-sm text-blue-600 font-medium">{uploadProgress}%</span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-2.5">
                                    <div
                                        className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                                        style={{ width: `${uploadProgress}%` }}
                                    ></div>
                                </div>
                            </div>
                        )}

                        <button
                            type="submit"
                            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition flex items-center justify-center"
                            disabled={loading}
                        >
                            {loading ? (
                                <span className="flex items-center">
                                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    جاري الإرسال...
                                </span>
                            ) : 'إرسال الإشعار'}
                        </button>
                    </div>
                </form>

                {/* قائمة الإشعارات */}
                <div className="bg-white rounded-lg shadow-md">
                    <div className="flex justify-between items-center p-4 border-b">
                        <h3 className="text-xl font-semibold">الإشعارات المرسلة</h3>
                        {notifications.length > 0 && (
                            <div className="text-sm text-gray-600">
                                المجموع: {notifications.length} إشعار
                            </div>
                        )}
                    </div>
                    <div className="divide-y">
                        {currentNotifications.length > 0 ? (
                            currentNotifications.map(notification => (
                                <div key={notification.id} className="p-4 hover:bg-gray-50">
                                    <div className="flex justify-between items-start">
                                        <div>
                                            <h4 className="font-semibold text-lg">{notification.title}</h4>
                                            <p className="text-sm text-blue-600 mb-1">الفصل: {notification.className}</p>
                                            {notification.studentNames && (
                                                <p className="text-sm text-blue-600 mb-1">التلاميذ: {notification.studentNames}</p>
                                            )}
                                            <p className="text-gray-600">{notification.message}</p>
                                            <p className="text-sm text-gray-500 mt-2">
                                                {notification.createdAt?.toLocaleDateString('ar-TN', {
                                                    year: 'numeric',
                                                    month: 'long',
                                                    day: 'numeric',
                                                    hour: '2-digit',
                                                    minute: '2-digit'
                                                })}
                                            </p>

                                            {/* Display attached file if exists */}
                                            {notification.attachment && (
                                                <div className="mt-3 p-2 bg-blue-50 border border-blue-200 rounded">
                                                    {notification.attachment.type.startsWith('image/') ? (
                                                        <div>
                                                            <img
                                                                src={notification.attachment.url}
                                                                alt={notification.attachment.name}
                                                                className="max-h-40 rounded cursor-pointer hover:opacity-90"
                                                                onClick={() => window.open(notification.attachment.url, '_blank')}
                                                            />
                                                            <p className="text-xs text-gray-600 mt-1">📎 {notification.attachment.name}</p>
                                                        </div>
                                                    ) : (
                                                        <a
                                                            href={notification.attachment.url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="flex items-center text-blue-600 hover:text-blue-800"
                                                        >
                                                            <svg className="w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                                                <path d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" />
                                                            </svg>
                                                            <span>📎 {notification.attachment.name}</span>
                                                            <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                                <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" />
                                                                <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z" />
                                                            </svg>
                                                        </a>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        <button
                                            onClick={() => handleDelete(notification)}
                                            className="text-red-500 hover:text-red-700 transition"
                                            title="حذف الإشعار"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                                <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <p className="text-center text-gray-500 p-4">لا توجد إشعارات</p>
                        )}
                    </div>

                    {/* Pagination Controls */}
                    {totalPages > 1 && (
                        <div className="p-4 border-t bg-gray-50">
                            <div className="flex items-center justify-between">
                                {/* عرض معلومات الصفحة */}
                                <div className="text-sm text-gray-600">
                                    عرض {startIndex + 1} - {Math.min(endIndex, notifications.length)} من {notifications.length}
                                </div>

                                {/* أزرار التنقل */}
                                <div className="flex gap-2 items-center">
                                    {/* زر الصفحة السابقة */}
                                    <button
                                        onClick={() => handlePageChange(currentPage - 1)}
                                        disabled={currentPage === 1}
                                        className={`px-3 py-1 rounded-lg transition ${currentPage === 1
                                            ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                            : 'bg-blue-600 text-white hover:bg-blue-700'
                                            }`}
                                    >
                                        السابق
                                    </button>

                                    {/* أرقام الصفحات */}
                                    <div className="flex gap-1">
                                        {[...Array(totalPages)].map((_, index) => {
                                            const pageNumber = index + 1;
                                            // عرض جميع الأرقام إذا كان العدد قليل، أو عرض الصفحات القريبة فقط
                                            if (
                                                totalPages <= 7 ||
                                                pageNumber === 1 ||
                                                pageNumber === totalPages ||
                                                (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                                            ) {
                                                return (
                                                    <button
                                                        key={pageNumber}
                                                        onClick={() => handlePageChange(pageNumber)}
                                                        className={`w-8 h-8 rounded-lg transition ${currentPage === pageNumber
                                                            ? 'bg-blue-600 text-white font-bold'
                                                            : 'bg-white text-gray-700 hover:bg-gray-200 border'
                                                            }`}
                                                    >
                                                        {pageNumber}
                                                    </button>
                                                );
                                            } else if (
                                                pageNumber === currentPage - 2 ||
                                                pageNumber === currentPage + 2
                                            ) {
                                                return <span key={pageNumber} className="px-1">...</span>;
                                            }
                                            return null;
                                        })}
                                    </div>

                                    {/* زر الصفحة التالية */}
                                    <button
                                        onClick={() => handlePageChange(currentPage + 1)}
                                        disabled={currentPage === totalPages}
                                        className={`px-3 py-1 rounded-lg transition ${currentPage === totalPages
                                            ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                            : 'bg-blue-600 text-white hover:bg-blue-700'
                                            }`}
                                    >
                                        التالي
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* نافذة تأكيد الحذف */}
            {showDeleteModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                        <h3 className="text-xl font-bold mb-4">تأكيد الحذف</h3>
                        <p className="mb-4">هل أنت متأكد من حذف هذا الإشعار؟</p>
                        <div className="flex justify-end gap-4">
                            <button
                                onClick={() => setShowDeleteModal(false)}
                                className="px-4 py-2 text-gray-600 hover:text-gray-800"
                            >
                                إلغاء
                            </button>
                            <button
                                onClick={confirmDelete}
                                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                            >
                                حذف
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default NotificationManagement;
